import { z } from 'zod'
import type {
  BannerConfig,
  StyledVariable,
  UrgencyConfig,
} from '@mc/certificates-design-system'

export const UrgencySchema: z.ZodType<UrgencyConfig> = z.record(
  z.string(),
  z.object({
    available_from: z
      .string()
      .refine((date) => !Number.isNaN(Date.parse(date)), 'Invalid date format'),
    text: z.string(),
  }),
)

export const StyledVariableSchema: z.ZodType<StyledVariable> = z.union([
  z.string(),
  z.array(
    z
      .object({
        style: z.string().optional(),
        copy: z.string(),
      })
      .strict(),
  ),
])

// Discount promo block shared by promo-variable payloads: percentage drives
// fixed sticky-footer copy, productCoupon feeds discount candidates to the
// offer endpoint. The lax base — `productCoupon` is optional here. Both surfaces
// that price a product (cert, subscription) tighten it identically with
// `.required({ productCoupon: true })`, since a payload without a coupon would
// advertise a percentage nothing is charging.
export const PromoSchema = z.object({
  name: z.string().optional(),
  percentage: z.number().min(0).max(100),
  coupon_code: z.string().optional(),
  terms: z.string().url().optional(),
  productCoupon: z
    .object({
      default: z.string(),
    })
    .catchall(z.string())
    .optional(),
})

// Canonical copy-block shapes, with three consumers: the default payload
// (below, each block `.optional()`), the per-surface overrides
// (ProductSurfaceCopySchema, each block `.partial()`), and
// applyProductSurface's completeness check on a merged block. Adding a field
// here propagates to all three — no re-declaring the shape. Kept as bare
// ZodObjects (not annotated `z.ZodType`) so `.partial()` / `.optional()` stay
// available.
export const CardBlockSchema = z.object({ badgeText: z.string() })

export const BannerBlockSchema = z.object({
  text: StyledVariableSchema,
  urgency_text: UrgencySchema.optional(),
  containerClassName: z.string().optional(),
  // 'primary-accent' is the deprecated alias for 'accent'. A payload with an
  // unlisted buttonKind is dropped whole (see README), so this has to keep
  // accepting it until no live flag emits it.
  buttonKind: z
    .enum(['tertiary', 'primary', 'secondary', 'accent', 'primary-accent'])
    .optional(),
  buttonArrowTheme: z
    .enum(['highlighted', 'dark', 'light', 'darkHighlighted'])
    .optional(),
})

export const ModalBlockSchema = z.object({
  image_16x9: z.string().url(),
  heading: StyledVariableSchema,
  body: StyledVariableSchema,
  urgency_text: UrgencySchema.optional(),
  use_cert_thumbnail: z.boolean().optional(),
})

export const CtaBlockSchema = z.object({
  text: StyledVariableSchema,
  urgency_text: UrgencySchema.optional(),
})

export const FooterBlockSchema = z.object({
  text: StyledVariableSchema,
  urgency_text: UrgencySchema.optional(),
})

export const PathSpotlightBlockSchema = z.object({ text: z.string() })

export const BannerSchema: z.ZodType<BannerConfig> = BannerBlockSchema

// A single set of copy-block overrides. Every block and field is optional
// (`.partial()` over the canonical block shapes): applyProductSurface merges the
// override over the default per block, so an override that sets only `heading`
// keeps the rest.
export const ProductSurfaceCopySchema = z
  .object({
    card: CardBlockSchema.partial(),
    banner: BannerBlockSchema.partial(),
    modal: ModalBlockSchema.partial(),
    cta: CtaBlockSchema.partial(),
    footer: FooterBlockSchema.partial(),
    pathSpotlight: PathSpotlightBlockSchema.partial(),
  })
  .partial()

// Overrides for one surface, split by release state (the two orthogonal axes:
// surface = where it renders, state = launched vs coming-soon vs pre-enroll).
// `default` layers over the top-level default copy for that surface;
// `comingSoon` layers on top of `default` for any not-yet-available product
// (plain coming-soon AND pre-enroll); `preEnroll` layers on top of
// `comingSoon` again, for the subset of those that are actually pre-enroll
// (see applyProductSurface's cascade). All optional — a surface with only a
// `comingSoon` (e.g. `cert`) inherits the top-level default when launched, and
// a pre-enroll product without a dedicated `preEnroll` override just inherits
// `comingSoon` copy unchanged.
export const SurfaceOverridesSchema = z.object({
  default: ProductSurfaceCopySchema.optional(),
  comingSoon: ProductSurfaceCopySchema.optional(),
  preEnroll: ProductSurfaceCopySchema.optional(),
})

// Subscription's productSurface override slice: banner + footer only, since
// SubscriptionPromoVariablesSchema carries no card/modal/cta blocks for those
// to ever apply to. Derived from ProductSurfaceCopySchema via `.pick()` so the
// block shapes stay single-sourced. No `comingSoon` key — unlike cert/bundle,
// subscription call sites never pass `comingSoon` to resolvePromoCopy, so that
// state is unreachable here; declaring it would let a GrowthBook override
// silently no-op.
export const SubscriptionSurfaceCopySchema = ProductSurfaceCopySchema.pick({
  banner: true,
  footer: true,
})

export const SubscriptionSurfaceOverridesSchema = z.object({
  default: SubscriptionSurfaceCopySchema.optional(),
})

// The per-certificate promo payload (`mccerts-promo-{slug}`). Stricter than the
// shared PromoSchema above: the promo block and its productCoupon are required —
// the same tightening the subscription payload applies, so both derive it the
// same way rather than restating the block. Lives here (not with the SSR fetcher
// / client hook that both consume it) so it stays free of server-only imports and
// safe to re-export from the client barrel.
export const CertificatePromoVariablesSchema = z.object({
  promo: PromoSchema.required({ productCoupon: true }),
  banner: BannerBlockSchema.optional(),
  modal: ModalBlockSchema.optional(),
  cta: CtaBlockSchema.optional(),
  footer: FooterBlockSchema.optional(),
  card: CardBlockSchema.optional(),
  pathSpotlight: PathSpotlightBlockSchema.optional(),
  checkout: z
    .object({
      urgency_text: UrgencySchema.optional(),
    })
    .optional(),
  // Site-wide payload only: per-surface, per-state copy overrides merged over the
  // default above by applyProductSurface (default ← surface.default ←
  // surface.comingSoon). Keyed by surface (cert/bundle/homepage/subscription —
  // subscription uses the narrower SubscriptionSurfaceOverridesSchema via
  // SubscriptionPromoVariablesSchema below). Absent on per-slug payloads.
  productSurface: z
    .object({
      cert: SurfaceOverridesSchema.optional(),
      bundle: SurfaceOverridesSchema.optional(),
      homepage: SurfaceOverridesSchema.optional(),
    })
    .optional(),
})

export type PromoVariables = z.infer<typeof CertificatePromoVariablesSchema>

// The homepage promo payload. Narrower than the cert payload: the homepage
// prices no product, so it only needs a banner plus the featured-certificate
// slug. Extracted from whichever payload wins (site-wide or per-surface).
export const HomepagePromoVariablesSchema = z.object({
  featured_certificate: z.string().optional(),
  banner: BannerSchema.optional(),
})

export type HomepagePromoVariables = z.infer<
  typeof HomepagePromoVariablesSchema
>

// The subscription (FCC) promo payload. `promo` **and its `productCoupon`** are
// required, like the cert payload's: the block carries the percentage the sticky
// footer renders and the coupon sent to the offer endpoint, so a payload missing
// either advertises a discount nothing is charging. That keeps payload presence
// equivalent to "a discount is configured" — the subscription surfaces have no
// membership logic of their own, so the AAP member treatment is just an
// AAP-targeted GrowthBook rule serving its own promo block. Reuses its own
// top-level blocks, optionally layered with a `productSurface.subscription`
// override (see SubscriptionSurfaceOverridesSchema) via applyProductSurface —
// mirrors the cert/bundle productSurface mechanism, scoped to banner/footer.
export const SubscriptionPromoVariablesSchema = z.object({
  banner: BannerSchema.optional(),
  footer: FooterBlockSchema.optional(),
  modal: ModalBlockSchema.optional(),
  promo: PromoSchema.required({ productCoupon: true }),
  productSurface: z
    .object({ subscription: SubscriptionSurfaceOverridesSchema.optional() })
    .optional(),
})

export type SubscriptionPromoVariables = z.infer<
  typeof SubscriptionPromoVariablesSchema
>

// The shape after resolvePromoCopy: `productSurface` overrides already merged
// in and stripped. Consumers below the page level should type their props
// with this, not SubscriptionPromoVariables, so passing the raw SSR payload
// straight through (skipping resolvePromoCopy) is a compile error rather than
// a silent no-op of the override.
export type ResolvedSubscriptionPromoVariables = Omit<
  SubscriptionPromoVariables,
  'productSurface'
>
